import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { ThirdPartyReference } from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { Color, forLifeOf, Form, LifecycleAware, Masks, Modal } from '@lsq-frontend/common-ui';
import { FeatureFlagService, Icons } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { first, map, startWith } from 'rxjs/operators';
import { ManageBuyerService } from '../manage-buyer.service';
import { AdminManageBuyerConfirmChangesModalComponent } from '../modals/confirm-changes-modal.component';
import { formatBpsInput, formatHbpsInput, handleBpsInput } from './buyer-configuration-helpers';
import { AdminManageBuyerConfigurationService } from './buyer-configuration.service';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';

@LifecycleAware()
@Component({
    selector: 'admin-buyer-configuration',
    styleUrls: ['./buyer-configuration-section.component.scss'],
    template: `
        <div class="row">
            <div class="col-12 col-lg-8">
                <card headerTitle="Buyer Configuration" [style.marginBottom]="'3rem'">
                    <div card-body *ngIf="!(formsInitialized$ | async)">
                        <lsq-circle-loader></lsq-circle-loader>
                    </div>
                    <div card-body *ngIf="formsInitialized$ | async">
                        <div class="buyer-configuration-section">
                            <div class="section-header">
                                <p class="body">Credit Limit Configuration</p>
                            </div>
                            <div [class]="{ 'section-body': true, open: true }">
                                <div [style.padding]="'2rem 3rem'">
                                    <form [formGroup]="creditLimitFormGroup">
                                        <p class="body" [style.marginBottom]="'1rem'">
                                            Please note that a valid Credit Limit and at least one
                                            fee rate (Default or Decaying) are required before
                                            completing implementation.
                                        </p>
                                        <div class="row">
                                            <div class="col-12 col-md-6 order-md-1">
                                                <lsq-label
                                                    [label]="
                                                        'Credit Limit' +
                                                        ((isImplementation$ | async) ? '' : '*')
                                                    "
                                                    subLabel="Buyer credit limit, input as dollars with cents."
                                                ></lsq-label>
                                            </div>
                                            <div class="col-12 col-md-6 order-md-3">
                                                <lsq-currency-input
                                                    [basicLabel]="true"
                                                    name="creditLimit"
                                                    formControlName="creditLimit"
                                                    [group]="creditLimitFormGroup"
                                                    [currencyMask]="currencyMask"
                                                >
                                                </lsq-currency-input>
                                            </div>
                                            <div class="col-12 col-md-6 order-md-2">
                                                <lsq-label
                                                    [label]="
                                                        'Credit Limit Currency' +
                                                        ((isImplementation$ | async) ? '' : '*')
                                                    "
                                                    subLabel="The currency the credit limit is set in."
                                                ></lsq-label>
                                            </div>
                                            <div class="col-12 col-md-6 order-md-4">
                                                <lsq-select
                                                    [basicLabel]="true"
                                                    name="creditLimitCurrency"
                                                    formControlName="creditLimitCurrency"
                                                    [group]="creditLimitFormGroup"
                                                    [options]="[{ label: 'USD', value: 'USD' }]"
                                                >
                                                </lsq-select>
                                            </div>
                                        </div>
                                    </form>
                                    <form [formGroup]="defaultFlatFeeFormGroup">
                                        <div class="row">
                                            <div class="col-12" [style.marginBottom]="'1rem'">
                                                <lsq-label
                                                    label="Default Flat Fee Rate"
                                                    subLabel="The fee rate all suppliers will have as default under this buyer. Set to percentage up to two decimal places."
                                                ></lsq-label>
                                                <lsq-input
                                                    [basicLabel]="true"
                                                    name="defaultFlatFeeRate"
                                                    formControlName="defaultFlatFeeRate"
                                                    [group]="defaultFlatFeeFormGroup"
                                                    [textFormatter]="formatBpsInput"
                                                    [handleKeypress]="handleBpsInput"
                                                    [mask]="bpsAsPercentMask"
                                                    [appendIcon]="[
                                                        icons.FaPercent,
                                                        color.GraphicGray
                                                    ]"
                                                >
                                                </lsq-input>
                                            </div>
                                        </div>
                                    </form>
                                    <form [formGroup]="decayingFeeFormGroup">
                                        <div class="row">
                                            <div class="col-12" [style.marginBottom]="'1rem'">
                                                <lsq-label
                                                    label="Decaying Fee Rate"
                                                    subLabel="The decaying fee rate all suppliers will have under this buyer as a daily rate. Set to whole percentage up to four decimal places."
                                                ></lsq-label>
                                                <lsq-input
                                                    [basicLabel]="true"
                                                    name="decayingFeeRate"
                                                    formControlName="decayingFeeRate"
                                                    [group]="decayingFeeFormGroup"
                                                    [textFormatter]="formatHbpsInput"
                                                    [handleKeypress]="handleBpsInput"
                                                    [mask]="hbpsAsPercentMask"
                                                    [appendIcon]="[
                                                        icons.FaPercent,
                                                        color.GraphicGray
                                                    ]"
                                                >
                                                </lsq-input>
                                            </div>
                                        </div>
                                    </form>
                                    <div class="divider"></div>
                                    <p class="body" [style.marginBottom]="'1rem'">
                                        Below are the available options that can be configured on a
                                        buyer. To enable an option switch the toggle and complete
                                        the configuration of the required fields and press save.
                                        Disabling an option will delete any data that has been
                                        configured upon saving.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <admin-buyer-configuration-grace-period
                            *ngIf="!!gracePeriodFormGroup"
                            [hasClickedSave]="hasClickedSave"
                            [gracePeriodFormGroup]="gracePeriodFormGroup"
                        ></admin-buyer-configuration-grace-period>

                        <admin-buyer-configuration-past-due
                            *ngIf="!!pastDueFeeFormGroup"
                            [hasClickedSave]="hasClickedSave"
                            [pastDueFeeFormGroup]="pastDueFeeFormGroup"
                        ></admin-buyer-configuration-past-due>

                        <admin-buyer-eligibility-settings
                            *ngIf="!!ineligibleDaysBeforeTermsFormGroup"
                            [hasClickedSave]="hasClickedSave"
                            [decayingFeeFormGroup]="decayingFeeFormGroup"
                            [hasClickedSave]="hasClickedSave"
                            [adjustable120DayFormGroup]="adjustable120DaysRuleFormGroup"
                            [maxDaysEarlyFormGroup]="maxDaysEarlyFormGroup"
                            [ineligibleDaysBeforeTermsFormGroup]="
                                ineligibleDaysBeforeTermsFormGroup
                            "
                        ></admin-buyer-eligibility-settings>

                        <admin-buyer-configuration-paid-fees
                            *ngIf="
                                !!buyerPaidEarlyPaymentFeesFormGroup &&
                                (buyerPaidEarlyPaymentPercentage$ | async)
                            "
                            [hasClickedSave]="hasClickedSave"
                            [buyerPaidEarlyPaymentFeesFormGroup]="
                                buyerPaidEarlyPaymentFeesFormGroup
                            "
                        ></admin-buyer-configuration-paid-fees>

                        <admin-buyer-configuration-paid-fees-visibility
                            *ngIf="
                                !!buyerPaidEarlyPaymentFeesVisibilityFormGroup &&
                                (buyerPaidEarlyPaymentPercentage$ | async)
                            "
                            [hasClickedSave]="hasClickedSave"
                            [buyerPaidEarlyPaymentFeesVisibilityFormGroup]="
                                buyerPaidEarlyPaymentFeesVisibilityFormGroup
                            "
                        ></admin-buyer-configuration-paid-fees-visibility>

                        <admin-buyer-configuration-paid-transfer-fees
                            *ngIf="
                                !!buyerPaidEarlyPaymentTransferFeesFormGroup &&
                                (buyerPaidEarlyPaymentTransferFee$ | async)
                            "
                            [hasClickedSave]="hasClickedSave"
                            [buyerPaidEarlyPaymentTransferFeesFormGroup]="
                                buyerPaidEarlyPaymentTransferFeesFormGroup
                            "
                        ></admin-buyer-configuration-paid-transfer-fees>

                        <admin-buyer-configuration-revenue-share
                            *ngIf="!!revenueShareFormGroup"
                            [hasClickedSave]="hasClickedSave"
                            [revenueShareFormGroup]="revenueShareFormGroup"
                        ></admin-buyer-configuration-revenue-share>

                        <admin-buyer-configuration-tpm-configuration
                            *ngIf="!!tpmFormGroup"
                            [hasClickedSave]="hasClickedSave"
                            [tpmFormGroup]="tpmFormGroup"
                        ></admin-buyer-configuration-tpm-configuration>

                        <admin-buyer-configuration-pay-later
                            *ngIf="(payLaterFeatureFlag$ | async) && !!payLaterFormGroup"
                            [hasClickedSave]="hasClickedSave"
                            [formGroup]="payLaterFormGroup"
                        >
                        </admin-buyer-configuration-pay-later>

                        <admin-buyer-configuration-ingest-all-invoices
                            *ngIf="!!ingestAllInvoicesFormGroup"
                            [hasClickedSave]="hasClickedSave"
                            [ingestAllInvoicesFormGroup]="ingestAllInvoicesFormGroup"
                        >
                        </admin-buyer-configuration-ingest-all-invoices>

                        <admin-buyer-configuration-infor-nexus-enabled
                            *ngIf="!!inforNexusEnabledFormGroup"
                            [hasClickedSave]="hasClickedSave"
                            [inforNexusEnabledFormGroup]="inforNexusEnabledFormGroup"
                        >
                        </admin-buyer-configuration-infor-nexus-enabled>

                        <div [style.padding]="'2rem 2rem 3rem 2rem'">
                            <div class="row">
                                <div
                                    class="col-12"
                                    *ngIf="hasFeeRateError"
                                    [style.marginBottom]="'1rem'"
                                >
                                    <p id="feeRateError" class="text-red">
                                        Must either have a Default Flat Rate Fee or Decaying Fee
                                        Rate set in order to save.
                                    </p>
                                </div>
                                <div class="col-12 col-md-3">
                                    <lsq-button
                                        appearance="secondary"
                                        size="block"
                                        (buttonClick)="clearChanges()"
                                        [disabled]="!(hasChanges$ | async)"
                                    >
                                        Clear Changes
                                    </lsq-button>
                                </div>
                                <div class="col-12 col-md-3">
                                    <lsq-button
                                        appearance="primary"
                                        size="block"
                                        (buttonClick)="saveChanges()"
                                        [disabled]="!(hasChanges$ | async)"
                                    >
                                        Save
                                    </lsq-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </card>
            </div>
            <div class="col-12 col-md-4">
                <data-card [lightBorder]="true">
                    <p class="header" [style.marginBottom]="'1rem'">Additional Information</p>
                    <p class="body text-dark-gray" [style.marginBottom]="'1rem'">
                        Please enter your updated information for the buyer and once you are done
                        click the Save button.
                    </p>
                    <p class="label">Default Flat Fee Rate</p>

                    <p
                        class="body text-dark-gray"
                        [style.marginBottom]="'1rem'"
                        [style.marginTop]="'1rem'"
                    >
                        This Program Type must be set in order to properly implement the buyer into
                        the system. If you need to set a <b>Decaying Fee Rate</b> to override this
                        value, this can be established in the following step.
                    </p>

                    <p class="label">Secondary Due Date Threshold</p>

                    <p
                        class="body text-dark-gray"
                        [style.marginBottom]="'1rem'"
                        [style.marginTop]="'1rem'"
                    >
                        If a buyer, contractually, does not have secondary due date threshold, then
                        secondary due date threshold should match the primary due date threshold.
                    </p>

                    <p class="label">Bank Accounts</p>

                    <p
                        class="body text-dark-gray"
                        [style.marginBottom]="'1rem'"
                        [style.marginTop]="'1rem'"
                    >
                        These are added to the buyer configuration for purposes of applying credits
                        back to the buyer when needed. Bank Accounts will also be needed when
                        implementing a buyer that uses Dynamic Discounting, which allows the user to
                        use their account for funding and providing early payments.
                    </p>
                </data-card>
            </div>
        </div>
    `
})
export class AdminManageBuyerConfigurationComponent implements OnInit, OnDestroy {
    public creditLimitFormGroup: FormGroup;
    public defaultFlatFeeFormGroup: FormGroup;
    public maxDaysEarlyFormGroup: FormGroup;
    public adjustable120DaysRuleFormGroup: FormGroup;
    public gracePeriodFormGroup: FormGroup;
    public pastDueFeeFormGroup: FormGroup;
    public buyerPaidEarlyPaymentFeesFormGroup: FormGroup;
    public buyerPaidEarlyPaymentFeesVisibilityFormGroup: FormGroup;
    public buyerPaidEarlyPaymentTransferFeesFormGroup: FormGroup;
    public decayingFeeFormGroup: FormGroup;
    public ineligibleDaysBeforeTermsFormGroup: FormGroup;
    public revenueShareFormGroup: FormGroup;
    public tpmFormGroup: FormGroup;
    public payLaterFormGroup: FormGroup;
    public ingestAllInvoicesFormGroup: FormGroup;
    public inforNexusEnabledFormGroup: FormGroup;

    public hasClickedSave = false;
    public hasFeeRateError = false;

    public isImplementation$ = this._manageBuyerService.isImplementation$;
    public formsInitialized$ = this._adminManageBuyerConfigurationService.formsInitialized$;
    public hasChanges$: Observable<boolean>;
    private _refetchSubject = new Subject();

    public readonly currencyMask = Masks.CurrencyMaskWithCents;
    public readonly icons = Icons;
    public readonly color = Color;
    public readonly bpsAsPercentMask = Masks.BpsAsPercentMask;
    public readonly formatBpsInput = formatBpsInput;
    public readonly formatHbpsInput = formatHbpsInput;
    public readonly handleBpsInput = handleBpsInput;

    public buyerPaidEarlyPaymentPercentage$ = this._featureFlagService.isOn(
        FeatureFlags.BuyerPaidEarlyPaymentPercentage
    );

    public buyerPaidEarlyPaymentTransferFee$ = this._featureFlagService.isOn(
        FeatureFlags.BuyerPaidEarlyPaymentTransferFee
    );

    public maxDaysEarlyFeatureFlag$ = this._featureFlagService.isOn(FeatureFlags.MaxDaysEarly);

    public payLaterFeatureFlag$ = this._featureFlagService.isOn(
        FeatureFlags.PayLaterAdministration
    );

    constructor(
        private _manageBuyerService: ManageBuyerService,
        private _adminManageBuyerConfigurationService: AdminManageBuyerConfigurationService,
        private _modalService: ModalService,
        private _alertService: AlertService,
        private _featureFlagService: FeatureFlagService
    ) {}

    public ngOnInit(): void {
        combineLatest([
            this._manageBuyerService.buyerKey$,
            this._refetchSubject.pipe(startWith(true))
        ])
            .pipe(forLifeOf(this))
            .subscribe(async ([buyerKey]) => {
                this.hasClickedSave = false;
                await this._adminManageBuyerConfigurationService.initializeFormGroups(buyerKey);
                this.creditLimitFormGroup =
                    this._adminManageBuyerConfigurationService.creditLimitFormGroup;
                this.defaultFlatFeeFormGroup =
                    this._adminManageBuyerConfigurationService.defaultFlatFeeFormGroup;
                this.maxDaysEarlyFormGroup =
                    this._adminManageBuyerConfigurationService.maxDaysEarlyFormGroup;
                this.adjustable120DaysRuleFormGroup =
                    this._adminManageBuyerConfigurationService.adjustable120DayFormGroup;
                this.gracePeriodFormGroup =
                    this._adminManageBuyerConfigurationService.gracePeriodFormGroup;
                this.pastDueFeeFormGroup =
                    this._adminManageBuyerConfigurationService.pastDueFeeFormGroup;
                this.buyerPaidEarlyPaymentFeesFormGroup =
                    this._adminManageBuyerConfigurationService.buyerPaidEarlyPaymentFeesFormGroup;
                this.buyerPaidEarlyPaymentFeesVisibilityFormGroup =
                    this._adminManageBuyerConfigurationService.buyerPaidEarlyPaymentFeeVisibilityFormGroup;
                this.buyerPaidEarlyPaymentTransferFeesFormGroup =
                    this._adminManageBuyerConfigurationService.buyerPaidEarlyPaymentTransferFeesFormGroup;
                this.decayingFeeFormGroup =
                    this._adminManageBuyerConfigurationService.decayingFeeFormGroup;
                this.ineligibleDaysBeforeTermsFormGroup =
                    this._adminManageBuyerConfigurationService.ineligibleDaysBeforeTermsFormGroup;
                this.revenueShareFormGroup =
                    this._adminManageBuyerConfigurationService.revenueShareFormGroup;
                this.tpmFormGroup = this._adminManageBuyerConfigurationService.tpmFormGroup;
                this.payLaterFormGroup =
                    this._adminManageBuyerConfigurationService.payLaterFormGroup;
                this.ingestAllInvoicesFormGroup =
                    this._adminManageBuyerConfigurationService.ingestAllInvoicesFormGroup;
                this.inforNexusEnabledFormGroup =
                    this._adminManageBuyerConfigurationService.inforNexusEnabledFormGroup;

                this.hasChanges$ = this._adminManageBuyerConfigurationService.hasChanges$;
            });
    }

    public ngOnDestroy(): void {
        this._adminManageBuyerConfigurationService.destroyForms();
    }

    public async clearChanges(): Promise<void> {
        this.hasClickedSave = false;
        this._adminManageBuyerConfigurationService.resetForms();
    }

    public async saveChanges(): Promise<void> {
        this.hasClickedSave = true;
        this.creditLimitFormGroup.markAllAsTouched();
        const isImplementation = await this.isImplementation$.pipe(first()).toPromise();

        this.hasFeeRateError =
            !isImplementation &&
            !this.defaultFlatFeeFormGroup.get('defaultFlatFeeRate').value &&
            !this.decayingFeeFormGroup.get('decayingFeeRate').value;

        this.defaultFlatFeeFormGroup.markAllAsTouched();

        if (this.maxDaysEarlyFormGroup.get('maxDaysEarlyEnabled').value) {
            this.maxDaysEarlyFormGroup.markAllAsTouched();
        }

        if (this.gracePeriodFormGroup.get('gracePeriodEnabled').value) {
            this.gracePeriodFormGroup.markAllAsTouched();
        }
        if (this.ingestAllInvoicesFormGroup.get('ingestAllInvoicesEnabled').value) {
            this.ingestAllInvoicesFormGroup.markAllAsTouched();
        }
        if (this.inforNexusEnabledFormGroup.get('inforNexusEnabled').value) {
            this.inforNexusEnabledFormGroup.markAllAsTouched();
        }
        if (this.pastDueFeeFormGroup.get('pastDueFeeEnabled').value) {
            this.pastDueFeeFormGroup.markAllAsTouched();
        }
        if (this.buyerPaidEarlyPaymentFeesFormGroup.get('buyerPaidEarlyPaymentFeesEnabled').value) {
            this.buyerPaidEarlyPaymentFeesFormGroup.markAllAsTouched();
        }
        if (
            this.buyerPaidEarlyPaymentFeesVisibilityFormGroup.get(
                'buyerPaidEarlyPaymentFeeVisibilityEnabled'
            ).value
        ) {
            this.buyerPaidEarlyPaymentFeesVisibilityFormGroup.markAllAsTouched();
        }

        if (
            this.buyerPaidEarlyPaymentTransferFeesFormGroup.get(
                'buyerPaidEarlyPaymentTransferFeesEnabled'
            ).value
        ) {
            this.buyerPaidEarlyPaymentTransferFeesFormGroup.markAllAsTouched();
        }
        this.decayingFeeFormGroup.markAllAsTouched();

        if (this.ineligibleDaysBeforeTermsFormGroup.get('ineligibleDaysBeforeTermsEnabled').value) {
            this.ineligibleDaysBeforeTermsFormGroup.markAllAsTouched();
        }

        if (this.adjustable120DaysRuleFormGroup.get('adjustable120DayEnabled').value) {
            this.adjustable120DaysRuleFormGroup.markAllAsTouched();
        }

        if (this.revenueShareFormGroup.get('enableRevenueShare').value) {
            this.revenueShareFormGroup.markAllAsTouched();
        }

        if (this.tpmFormGroup.get('tpmEnabled').value) {
            this.tpmFormGroup.markAllAsTouched();

            if (
                !this.tpmFormGroup.value.achRoutingNumber &&
                !this.tpmFormGroup.value.wireRoutingNumber &&
                (await this._adminManageBuyerConfigurationService.isUpdatingTpmBankAccount$
                    .pipe(first())
                    .toPromise())
            ) {
                this.tpmFormGroup.get('achRoutingNumber').setErrors({
                    atLeastOne: { message: 'Must either have an ACH or Wire Routing number' }
                });
                this.tpmFormGroup.get('wireRoutingNumber').setErrors({
                    atLeastOne: { message: 'Must either have an ACH or Wire Routing number' }
                });
            } else {
                this.tpmFormGroup.get('achRoutingNumber').setErrors(null);
                this.tpmFormGroup.get('wireRoutingNumber').setErrors(null);
                this.tpmFormGroup.get('achRoutingNumber').updateValueAndValidity();
                this.tpmFormGroup.get('wireRoutingNumber').updateValueAndValidity();
            }
        }

        if (this.payLaterFormGroup.get('payLaterEnabled').value) {
            this.payLaterFormGroup.markAllAsTouched();
        }

        if (
            !this.hasFeeRateError &&
            this.creditLimitFormGroup.valid &&
            this.defaultFlatFeeFormGroup.valid &&
            this._hasFormsDisabledOrValid({
                maxDaysEarlyEnabled: this.maxDaysEarlyFormGroup,
                gracePeriodEnabled: this.gracePeriodFormGroup,
                pastDueFeeEnabled: this.pastDueFeeFormGroup,
                buyerPaidEarlyPaymentFeesEnabled: this.buyerPaidEarlyPaymentFeesFormGroup,
                buyerPaidEarlyPaymentTransferFeesEnabled:
                    this.buyerPaidEarlyPaymentTransferFeesFormGroup,
                ineligibleDaysBeforeTermsEnabled: this.ineligibleDaysBeforeTermsFormGroup,
                enableRevenueShare: this.revenueShareFormGroup,
                tpmEnabled: this.tpmFormGroup,
                payLaterEnabled: this.payLaterFormGroup,
                ingestAllInvoicesEnabled: this.ingestAllInvoicesFormGroup,
                inforNexusEnabled: this.inforNexusEnabledFormGroup,
                adjustable120DayEnabled: this.adjustable120DaysRuleFormGroup
            })
        ) {
            const _isSubmittingSubject = new BehaviorSubject<boolean>(false);

            this._modalService.showModal(
                new Modal({
                    title: 'Update Company Configuration',
                    bodyComponent: AdminManageBuyerConfirmChangesModalComponent,
                    isSubmitting$: _isSubmittingSubject.asObservable(),
                    showFooter: true,
                    showCancelButton: true,
                    showCloseButton: true,
                    showOkButton: true,
                    width: '900px',
                    okButtonSize: 'md',
                    okButtonText: 'Confirm Update',
                    data: {
                        changes: await combineLatest([
                            this._adminManageBuyerConfigurationService.creditLimitChanges$,
                            this._adminManageBuyerConfigurationService.defaultFlatFeeChanges$,
                            this._adminManageBuyerConfigurationService.maxDaysEarlyChanges$,
                            this._adminManageBuyerConfigurationService.gracePeriodChanges$,
                            this._adminManageBuyerConfigurationService.pastDueFeeChanges$,
                            this._adminManageBuyerConfigurationService
                                .buyerPaidEarlyPaymentFeesChanges$,
                            this._adminManageBuyerConfigurationService
                                .buyerPaidEarlyPaymentFeesVisibilityChanges$,
                            this._adminManageBuyerConfigurationService
                                .buyerPaidEarlyPaymentTransferFeesChanges$,
                            this._adminManageBuyerConfigurationService.decayingFeeChanges$,
                            this._adminManageBuyerConfigurationService
                                .ineligibleDaysBeforeTermsChanges$,
                            this._adminManageBuyerConfigurationService.adjustable120DaysChanges$,
                            this._adminManageBuyerConfigurationService
                                .revenueShareConfigurationChanges$,
                            this._adminManageBuyerConfigurationService.tpmConfigurationChanges$,
                            this._adminManageBuyerConfigurationService.payLaterChanges$,
                            this._adminManageBuyerConfigurationService.ingestAllInvoicesChanges$,
                            this._adminManageBuyerConfigurationService.inforNexusEnabledChanges$
                        ])
                            .pipe(
                                map(
                                    ([
                                        creditLimitChanges,
                                        defaultFlatFeeChanges,
                                        maxDaysEarlyChanges,
                                        gracePeriodChanges,
                                        pastDueFeeChanges,
                                        buyerPaidEarlyPaymentFeesChanges,
                                        buyerPaidEarlyPaymentFeesVisibilityChanges,
                                        buyerPaidEarlyPaymentTransferFeesChanges,
                                        decayingFeeChanges,
                                        ineligibleDaysBeforeTermsChanges,
                                        adjustable120DaysChanges,
                                        revenueShareConfigurationChanges,
                                        tpmConfigurationChanges,
                                        payLaterConfigurationChanges,
                                        ingestAllInvoicesChanges,
                                        inforNexusEnabledChanges
                                    ]) => [
                                        ...creditLimitChanges,
                                        ...defaultFlatFeeChanges,
                                        ...maxDaysEarlyChanges,
                                        ...gracePeriodChanges,
                                        ...pastDueFeeChanges,
                                        ...buyerPaidEarlyPaymentFeesChanges,
                                        ...buyerPaidEarlyPaymentFeesVisibilityChanges,
                                        ...buyerPaidEarlyPaymentTransferFeesChanges,
                                        ...decayingFeeChanges,
                                        ...ineligibleDaysBeforeTermsChanges,
                                        ...adjustable120DaysChanges,
                                        ...revenueShareConfigurationChanges,
                                        ...tpmConfigurationChanges,
                                        ...payLaterConfigurationChanges,
                                        ...ingestAllInvoicesChanges,
                                        ...inforNexusEnabledChanges
                                    ]
                                ),
                                first()
                            )
                            .toPromise()
                    },
                    onOkClick: async () => {
                        _isSubmittingSubject.next(true);
                        try {
                            await this._adminManageBuyerConfigurationService.commitChanges();
                            this._modalService.closeModal();

                            this._refetchSubject.next();
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                            this._alertService.success('Buyer configuration successfully updated.');
                        } catch {
                            this._alertService.error('An error ocurred while updating.');
                        }

                        _isSubmittingSubject.next(false);
                    }
                })
            );
        }
    }

    private _hasFormsDisabledOrValid(formsMap: { [enablerKey: string]: FormGroup }): boolean {
        return Object.keys(formsMap).every(
            (key) => !formsMap[key].get(key).value || formsMap[key].valid
        );
    }
}
